import { useState, useMemo } from "react";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidateFinancials } from "@/lib/invalidateProjectFinancials";
import { useAuth } from "@/contexts/AuthContext";
import { RequirePermission } from "@/components/RequirePermission";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Receipt, Filter, Loader2, Plus, Pencil } from "lucide-react";
import { ReceiptDropZone } from "@/components/controller/ReceiptDropZone";
import { AccountSelect } from "@/components/controller/AccountSelect";
import { useChartOfAccounts } from "@/hooks/useChartOfAccounts";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { ExportToolbar } from "@/components/shared/ExportToolbar";
import { PageActions } from "@/components/shared/PageActions";
import { toast } from "sonner";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

// Legacy categories kept for display of old data
const LEGACY_CATEGORIES: Record<string, string> = {
  labor: "Labor", material: "Material", equipment: "Equipment", travel: "Travel",
  permits: "Permits & Fees", subcontractor: "Subcontractor", fuel: "Fuel", tools: "Tools", other: "Other",
};

const PAYMENT_METHODS: Record<string, string> = {
  cash: "Cash",
  check: "Check",
  credit_card: "Credit Card",
  ach: "ACH",
  debit_card: "Debit Card",
  other: "Other",
};

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  pending: { label: "Pending", className: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  approved: { label: "Approved", className: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" },
  reimbursed: { label: "Reimbursed", className: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
};

const EMPTY_FORM = {
  description: "",
  amount: "",
  expense_date: new Date().toISOString().split("T")[0],
  account_id: null as string | null,
  vendor_name: "",
  notes: "",
  project_id: "",
  receiptFile: null as File | null,
};

const ExpensesInner = () => {
  const navigate = useTabNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: accounts } = useChartOfAccounts();

  // Build a lookup for account names
  const accountMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of accounts ?? []) {
      map.set(a.id, a.account_number ? `${a.account_number} — ${a.name}` : a.name);
    }
    return map;
  }, [accounts]);

  const getAccountLabel = (row: any) => {
    if (row.account_id && accountMap.has(row.account_id)) return accountMap.get(row.account_id)!;
    if (row.category) return LEGACY_CATEGORIES[row.category] ?? row.category;
    return "—";
  };

  const [projectFilter, setProjectFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<any | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [busy, setBusy] = useState(false);

  const { data: expenses, isLoading } = useQuery({
    queryKey: ["all_expenses", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_expenses")
        .select("*, projects(name, project_number), vendors(name)")
        .order("expense_date", { ascending: false })
        .limit(1000);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });

  const { data: projects } = useQuery({
    queryKey: ["projects_list_for_expenses"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, name, project_number")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });

  // Derive unique projects and vendors for filter dropdowns
  const projectOptions = useMemo(() => {
    const map = new Map<string, string>();
    (expenses ?? []).forEach((e: any) => {
      if (e.project_id && e.projects?.name) map.set(e.project_id, `${e.projects.project_number ?? ""} ${e.projects.name}`.trim());
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [expenses]);

  const vendorOptions = useMemo(() => {
    const map = new Map<string, string>();
    (expenses ?? []).forEach((e: any) => {
      if (e.vendor_id && e.vendors?.name) map.set(e.vendor_id, e.vendors.name);
      else if (e.vendor_name) map.set(e.vendor_name, e.vendor_name);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [expenses]);

  const filtered = useMemo(() => {
    let rows = expenses ?? [];
    if (projectFilter !== "all") rows = rows.filter((r: any) => r.project_id === projectFilter);
    if (vendorFilter !== "all") rows = rows.filter((r: any) => r.vendor_id === vendorFilter || r.vendor_name === vendorFilter);
    if (categoryFilter !== "all") rows = rows.filter((r: any) => r.account_id === categoryFilter || r.category === categoryFilter);
    if (statusFilter !== "all") rows = rows.filter((r: any) => r.status === statusFilter);
    if (dateFrom) rows = rows.filter((r: any) => new Date(r.expense_date) >= dateFrom);
    if (dateTo) rows = rows.filter((r: any) => new Date(r.expense_date) <= dateTo);
    return rows;
  }, [expenses, projectFilter, vendorFilter, categoryFilter, statusFilter, dateFrom, dateTo]);

  const totalAll = useMemo(() => filtered.reduce((s: number, r: any) => s + Number(r.amount), 0), [filtered]);
  const approvedTotal = useMemo(() => filtered.filter((r: any) => r.status === "approved" || r.status === "reimbursed").reduce((s: number, r: any) => s + Number(r.amount), 0), [filtered]);
  const pendingTotal = useMemo(() => filtered.filter((r: any) => r.status === "pending").reduce((s: number, r: any) => s + Number(r.amount), 0), [filtered]);

  const hasActiveFilters = projectFilter !== "all" || vendorFilter !== "all" || categoryFilter !== "all" || statusFilter !== "all" || !!dateFrom || !!dateTo;

  const clearFilters = () => {
    setProjectFilter("all");
    setVendorFilter("all");
    setCategoryFilter("all");
    setStatusFilter("all");
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const handleAddExpense = async () => {
    if (!form.project_id) return toast.error("Select a project");
    if (!form.description.trim()) return toast.error("Description is required");
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) return toast.error("Enter a valid amount");

    setBusy(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const { data: inserted, error } = await supabase.from("project_expenses").insert({
        company_id: companyId,
        project_id: form.project_id,
        description: form.description.trim(),
        amount,
        expense_date: form.expense_date,
        account_id: form.account_id || null,
        vendor_name: form.vendor_name.trim() || null,
        notes: form.notes.trim() || null,
        submitted_by: user?.id,
        status: "pending",
      }).select("id").single();
      if (error) throw error;

      if (form.receiptFile && inserted?.id) {
        const ext = form.receiptFile.name.split(".").pop();
        const path = `${companyId}/${inserted.id}/receipt.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from("expense-receipts")
          .upload(path, form.receiptFile, { upsert: true });
        if (uploadErr) {
          toast.error("Expense saved but receipt upload failed");
        } else {
          await supabase.from("project_expenses")
            .update({ receipt_path: path })
            .eq("id", inserted.id);
        }
      }

      toast.success("Expense added");
      setForm({ ...EMPTY_FORM });
      setAddOpen(false);
      invalidateFinancials(qc, { projectId: form.project_id });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (row: any) => {
    setEditingExpense(row);
    setForm({
      description: row.description || "",
      amount: String(row.amount ?? ""),
      expense_date: row.expense_date || new Date().toISOString().split("T")[0],
      account_id: row.account_id || null,
      vendor_name: row.vendor_name || "",
      notes: row.notes || "",
      project_id: row.project_id || "",
      receiptFile: null,
    });
    setAddOpen(true);
  };

  const handleSaveExpense = async () => {
    if (!form.project_id) return toast.error("Select a project");
    if (!form.description.trim()) return toast.error("Description is required");
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) return toast.error("Enter a valid amount");

    setBusy(true);
    try {
      const { error } = await supabase.from("project_expenses").update({
        project_id: form.project_id,
        description: form.description.trim(),
        amount,
        expense_date: form.expense_date,
        account_id: form.account_id || null,
        vendor_name: form.vendor_name.trim() || null,
        notes: form.notes.trim() || null,
      }).eq("id", editingExpense.id);
      if (error) throw error;

      if (form.receiptFile) {
        const { data: companyId } = await supabase.rpc("my_company_id");
        if (companyId) {
          const ext = form.receiptFile.name.split(".").pop();
          const path = `${companyId}/${editingExpense.id}/receipt.${ext}`;
          const { error: uploadErr } = await supabase.storage
            .from("expense-receipts")
            .upload(path, form.receiptFile, { upsert: true });
          if (uploadErr) {
            toast.error("Expense updated but receipt upload failed");
          } else {
            await supabase.from("project_expenses")
              .update({ receipt_path: path })
              .eq("id", editingExpense.id);
          }
        }
      }

      toast.success("Expense updated");
      closeDialog();
      invalidateFinancials(qc, { projectId: form.project_id });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const closeDialog = () => {
    setAddOpen(false);
    setEditingExpense(null);
    setForm({ ...EMPTY_FORM });
  };

  const isEditing = !!editingExpense;

  const filterControls = (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={projectFilter} onValueChange={setProjectFilter}>
        <SelectTrigger className="w-44 h-8"><SelectValue placeholder="Project" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Projects</SelectItem>
          {projectOptions.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={vendorFilter} onValueChange={setVendorFilter}>
        <SelectTrigger className="w-40 h-8"><SelectValue placeholder="Vendor" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Vendors</SelectItem>
          {vendorOptions.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={categoryFilter} onValueChange={setCategoryFilter}>
        <SelectTrigger className="w-44 h-8"><SelectValue placeholder="Account" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Accounts</SelectItem>
          {(accounts ?? []).map(a => <SelectItem key={a.id} value={a.id}>{a.account_number ? `${a.account_number} — ` : ""}{a.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-36 h-8"><SelectValue placeholder="Status" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="pending">Pending</SelectItem>
          <SelectItem value="approved">Approved</SelectItem>
          <SelectItem value="rejected">Rejected</SelectItem>
          <SelectItem value="reimbursed">Reimbursed</SelectItem>
        </SelectContent>
      </Select>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className={cn("h-8 text-xs", dateFrom && "border-primary")}>
            <CalendarIcon className="h-3.5 w-3.5 mr-1" />
            {dateFrom ? format(dateFrom, "MMM d") : "From"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} className="p-3 pointer-events-auto" />
        </PopoverContent>
      </Popover>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className={cn("h-8 text-xs", dateTo && "border-primary")}>
            <CalendarIcon className="h-3.5 w-3.5 mr-1" />
            {dateTo ? format(dateTo, "MMM d") : "To"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={dateTo} onSelect={setDateTo} className="p-3 pointer-events-auto" />
        </PopoverContent>
      </Popover>
      {hasActiveFilters && (
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearFilters}>Clear</Button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Expenses</h1>
        <div className="flex items-center gap-2">
          {filtered.length > 0 && <ExportToolbar entityType="expenses" data={filtered} />}
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add Expense
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3 space-y-0.5">
            <p className="text-xs text-muted-foreground">Total Expenses</p>
            <p className="font-semibold text-lg">{fmt(totalAll)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 space-y-0.5">
            <p className="text-xs text-muted-foreground">Approved</p>
            <p className="font-semibold text-lg text-green-700 dark:text-green-400">{fmt(approvedTotal)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 space-y-0.5">
            <p className="text-xs text-muted-foreground">Pending</p>
            <p className="font-semibold text-lg text-amber-700 dark:text-amber-400">{fmt(pendingTotal)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 space-y-0.5">
            <p className="text-xs text-muted-foreground">Count</p>
            <p className="font-semibold text-lg">{filtered.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      {isMobile ? (
        <>
          <Button variant="outline" size="sm" className="h-8" onClick={() => setFiltersOpen(!filtersOpen)}>
            <Filter className="h-3.5 w-3.5 mr-1" /> Filters
            {hasActiveFilters && <span className="ml-1 w-2 h-2 rounded-full bg-primary" />}
          </Button>
          {filtersOpen && filterControls}
        </>
      ) : (
        filterControls
      )}

      <span className="text-xs text-muted-foreground">{filtered.length} expense{filtered.length !== 1 ? "s" : ""}</span>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Receipt className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No expenses found.</p>
        </div>
      ) : isMobile ? (
        <div className="space-y-3">
          {filtered.map((row: any) => {
            const badge = STATUS_BADGE[row.status] ?? STATUS_BADGE.pending;
            const catLabel = getAccountLabel(row);
            const projectName = row.projects ? `${row.projects.project_number ?? ""} ${row.projects.name}`.trim() : "—";
            return (
              <Card key={row.id} className="cursor-pointer active:bg-muted/50" onClick={() => navigate(`/controller/projects/${row.project_id}?tab=expenses`)}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm truncate flex-1">{row.description}</span>
                    <Badge variant="outline" className={cn("text-[10px] ml-2 shrink-0", badge.className)}>{badge.label}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{format(new Date(row.expense_date), "MMM d, yyyy")}</span>
                    <Badge variant="outline" className="text-[10px]">{catLabel}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground truncate">{projectName}</span>
                    <span className="text-lg font-bold">{fmt(Number(row.amount))}</span>
                  </div>
                  {(row.vendors?.name || row.vendor_name) && (
                    <span className="text-xs text-muted-foreground">Vendor: {row.vendors?.name ?? row.vendor_name}</span>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Vendor / Payee</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row: any) => {
                const badge = STATUS_BADGE[row.status] ?? STATUS_BADGE.pending;
                const catLabel = getAccountLabel(row);
                const projectName = row.projects ? `${row.projects.project_number ?? ""} ${row.projects.name}`.trim() : "—";
                const vendorDisplay = row.vendors?.name ?? row.vendor_name ?? "—";
                return (
                  <TableRow
                    key={row.id}
                    className={cn("cursor-pointer hover:bg-muted/50", row.status === "rejected" && "opacity-60")}
                    onClick={() => navigate(`/controller/projects/${row.project_id}?tab=expenses`)}
                  >
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {format(new Date(row.expense_date), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-sm">{vendorDisplay}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{catLabel}</Badge>
                    </TableCell>
                    <TableCell className="font-medium text-sm max-w-[200px] truncate">{row.description}</TableCell>
                    <TableCell className="text-right font-semibold">{fmt(Number(row.amount))}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{PAYMENT_METHODS[row.payment_method] ?? row.payment_method ?? "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[160px] truncate">{projectName}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("text-[10px]", badge.className)}>{badge.label}</Badge>
                    </TableCell>
                    <TableCell>
                      {!row.bill_id && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title="Edit" onClick={(e) => { e.stopPropagation(); openEdit(row); }}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add / Edit Expense Dialog */}
      <Dialog open={addOpen} onOpenChange={(v) => { if (!v) closeDialog(); else setAddOpen(v); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" /> {isEditing ? "Edit Expense" : "Add Expense"}
            </DialogTitle>
            <DialogDescription>{isEditing ? "Update the expense details below." : "Record an expense and link it to a project."}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Project *</label>
              <Select value={form.project_id} onValueChange={v => setForm(f => ({ ...f, project_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {(projects ?? []).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.project_number ? `${p.project_number} — ` : ""}{p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Description *</label>
              <Input
                placeholder="e.g. Permit fee — City of Austin"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Amount *</label>
                <Input
                  type="number" step="0.01" min="0" placeholder="0.00"
                  value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Date *</label>
                <Input
                  type="date"
                  value={form.expense_date}
                  onChange={e => setForm(f => ({ ...f, expense_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Account</label>
                <AccountSelect
                  value={form.account_id}
                  onValueChange={v => setForm(f => ({ ...f, account_id: v }))}
                  placeholder="Select account"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Vendor / Payee</label>
                <Input
                  placeholder="Who was paid"
                  value={form.vendor_name}
                  onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea
                placeholder="Additional details, reference number, etc."
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>

            <ReceiptDropZone
              file={form.receiptFile}
              onFileChange={f => setForm(prev => ({ ...prev, receiptFile: f }))}
              pasteActive={addOpen}
            />
            {isEditing && editingExpense.receipt_path && !form.receiptFile && (
              <p className="text-xs text-muted-foreground">Current receipt on file. Upload a new one to replace it.</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={isEditing ? handleSaveExpense : handleAddExpense} disabled={busy}>
              {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />{isEditing ? "Saving…" : "Adding…"}</> : isEditing ? "Save Changes" : "Add Expense"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Expenses = () => (
  <RequirePermission anyOf={["view_financials", "manage_expenses", "submit_expenses"]}>
    <ExpensesInner />
  </RequirePermission>
);

export default Expenses;
